import { filter } from 'rxjs/operators';
import { mobileLinksAnimation, searchAnimation } from './navAnimations';
import {
  Destination,
  DestinationService,
  Article,
  ArticleService,
  ArticleSearchModel,
  ArticleCategory,
  ArticleType,
} from '../admin/shared/public-api';
import { NavService } from './service/nav.service';
import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  AfterViewInit,
  HostListener,
  inject,
  DOCUMENT,
} from '@angular/core';
import { forkJoin, fromEvent, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { NavigationStart, Router, RouterModule } from '@angular/router';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { DeviceService } from '../shared/services/device.service';
import { ArticleCardComponent } from '../shared/components/article-card/article-card.component';

@Component({
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss'],
  animations: [searchAnimation, mobileLinksAnimation],
  imports: [CommonModule, ArticleCardComponent, RouterModule, NgOptimizedImage],
})
export class NavComponent implements OnInit, AfterViewInit {
  @ViewChild('searchInput') searchInput: ElementRef;
  private document = inject(DOCUMENT);
  protected readonly deviceService = inject(DeviceService);
  protected readonly navService = inject(NavService);
  private destinationService = inject(DestinationService);
  private articleService = inject(ArticleService);

  destinations: Destination[];
  hoveredDestination?: Destination;
  search = 'unsearch';
  searchResults: Article[];
  searchMessage = '';
  hasSearched = false;
  showMobileMenu = false;
  showMobileDestination = false;
  showMobileRoadtrip = false;
  showMobileBelgique = false;
  showLang = false;
  roadTripArticles: { [destinationId: string]: { destination: Destination; articles: Article[] } } =
    {};

  ArticleCategory = ArticleCategory;
  belgiumDestinationId: string;

  constructor(router: Router) {
    router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe((e) => {
      this.showMobileMenu = false;
    });
  }

  ngAfterViewInit(): void {
    fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        filter(Boolean),
        debounceTime(500),
        distinctUntilChanged(),
        tap(() => this.onSearch(this.searchInput.nativeElement.value)),
      )
      .subscribe();
  }

  ngOnInit(): void {
    const allArticles$ = this.articleService.getArticles(0, 10000);
    const destinations$ = this.destinationService.getDestinations();

    forkJoin({ allArticles: allArticles$, destinations: destinations$ }).subscribe(
      ({ allArticles, destinations }) => {
        this.destinations = destinations.sort((a, b) => a.country.localeCompare(b.country));
        this.belgiumDestinationId = this.destinations.find((x) => x.country === 'Belgique')?.id;
        this.hoveredDestination = this.destinations[0];
        allArticles
          .filter((x) => x.category === ArticleCategory.RoadTrip)
          .forEach((x) => {
            if (x.destination) {
              if (this.roadTripArticles[x.destination.id] === undefined) {
                this.roadTripArticles[x.destination.id] = {
                  destination: x.destination,
                  articles: [],
                };
              }
              this.roadTripArticles[x.destination.id].articles.push(x);
            } else {
              if (this.roadTripArticles['none'] === undefined) {
                this.roadTripArticles['none'] = {
                  destination: null,
                  articles: [],
                };
              }
              this.roadTripArticles['none'].articles.push(x);
            }
          });
      },
    );
  }

  @HostListener('window:scroll')
  windowListener(): void {
    if (this.showMobileMenu) {
      return;
    }
    if (window.scrollY > 0) {
      this.navService.setBackgroundColor('#3a5d4d');
    } else {
      this.navService.setBackgroundColor(this.navService.initialColor());
    }
  }

  hoverDestination(destination: Destination): void {
    this.hoveredDestination = destination;
  }

  removeAnim(): void {
    const menus = this.document.getElementsByClassName('pop-menu');
    for (let i = 0; i < menus.length; i++) {
      const menu = menus[i];
      const links = menu.getElementsByClassName('link');

      for (let p = 0; p < links.length; p++) {
        const link = links[p];
        link.classList.add('no-anim');
      }
    }
  }

  hovered(event: MouseEvent): void {
    (event.target as HTMLElement).classList.remove('no-anim');
  }

  onSearch(searchWord: string): void {
    this.searchMessage = '';
    if (searchWord === '') {
      this.hasSearched = false;
      this.searchResults = [];
      return;
    }
    const searchModel = new ArticleSearchModel();
    searchModel.searchWord = searchWord;
    this.searchArticles(searchModel);
  }

  searchArticles(searchModel: ArticleSearchModel): void {
    this.articleService.searchArticle(searchModel).subscribe((articles: Article[]) => {
      this.hasSearched = true;
      timer(600).subscribe(() => {
        if (articles.length === 0) {
          this.searchMessage = `Pas de résultat...`;
        }
        this.searchResults = articles;
      });
    });
  }

  openSearch(): void {
    if (this.searchInput) {
      this.searchInput.nativeElement.focus();
    }
    this.search = 'search';
  }

  closeSearch(anim = true): void {
    this.searchMessage = '';
    if (!anim) {
      this.search = 'unsearchNoAnim';
    } else {
      this.search = 'unsearch';
    }
    this.hasSearched = false;
    this.searchInput.nativeElement.value = null;
    this.searchResults = [];
  }

  showMenu(): void {
    this.showMobileMenu = !this.showMobileMenu;
    if (this.showMobileMenu) {
      this.navService.setBackgroundColor('#3a5d4d');
    } else {
      this.navService.setBackgroundColor(this.navService.initialColor());
    }
  }

  showDestination(): void {
    if (this.deviceService.isDesktop()) {
      return;
    }
    this.showMobileDestination = !this.showMobileDestination;
  }

  toggleSubMenu(menu: 'roadtrip' | 'belgique', event?: Event): void {
    if (this.deviceService.isDesktop()) {
      return;
    }
    if (event) {
      event.preventDefault();
    }
    if (menu === 'roadtrip') {
      this.showMobileRoadtrip = !this.showMobileRoadtrip;
    } else if (menu === 'belgique') {
      this.showMobileBelgique = !this.showMobileBelgique;
    }
  }
}
